// src/pages/HMRCTax.js
// Customer-facing view of the figures HMRC holds:
//   - official in-year tax calculation (Individual Calculations MTD v8.0)
//   - MTD for Income Tax status        (SA Individual Details MTD v2.0)
//   - amount owed to HMRC              (Self Assessment Accounts MTD v4.0)
//
// Everything here comes from HMRC. The estimates elsewhere in Quarterly are
// calculated locally and are labelled as such.

import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../components/AppContext'
import { supabase } from '../lib/supabase'
import { getDeviceId } from '../lib/device'
import {
  RefreshCw, CheckCircle, AlertCircle, Calculator,
  ShieldCheck, ExternalLink, FileCheck,
} from 'lucide-react'

// HMRC's sandbox returns sentinel placeholder amounts (e.g. -99999999999999.99).
// Anything of an implausible magnitude is treated as "not available" rather than
// rendered as a real figure.
function isRealAmount(n) {
  if (n === null || n === undefined || n === '') return false
  const v = Number(n)
  if (!Number.isFinite(v)) return false
  // No sole trader has a figure of this magnitude. Anything larger is a
  // sandbox placeholder or bad data and must not be shown as a tax figure.
  if (Math.abs(v) >= 1000000000) return false
  return true
}

function money(n) {
  if (!isRealAmount(n)) return '\u2014'
  return '£' + Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const shortDate = d => {
  if (!d) return null
  const parsed = new Date(d)
  if (isNaN(parsed)) return null
  // Sandbox placeholder dates (e.g. 1920) are not shown to the customer.
  const y = parsed.getFullYear()
  if (y < 2015 || y > 2100) return null
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function currentTaxYear() {
  const now = new Date()
  const m = now.getMonth() + 1, d = now.getDate()
  const start = (m < 4 || (m === 4 && d < 6)) ? now.getFullYear() - 1 : now.getFullYear()
  return `${start}-${String(start + 1).slice(2)}`
}

// HMRC fraud prevention requires the client's public (ephemeral) port, which
// browsers only expose through a WebRTC STUN candidate.
async function getClientPublicPort() {
  return new Promise(resolve => {
    let port = ''
    try {
      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] })
      pc.createDataChannel('')
      pc.createOffer().then(o => pc.setLocalDescription(o)).catch(() => {})
      pc.onicecandidate = e => {
        const m = e.candidate?.candidate?.match(/udp\s+\d+\s+[\d.]+\s+(\d+)/i)
        if (m) { port = m[1]; try { pc.close() } catch {} ; resolve(port) }
      }
      setTimeout(() => resolve(port), 3000)
    } catch { resolve('') }
  })
}

export default function HMRCTax() {
  const { user, profile, loadProfile } = useAuth()

  const [taxYear]                 = useState(currentTaxYear())
  const [calc, setCalc]           = useState(null)
  const [calcLoading, setCalcLoading] = useState(false)
  const [calcError, setCalcError] = useState('')

  const [itsa, setItsa]           = useState(null)
  const [submitted, setSubmitted] = useState(null)
  const [sideLoading, setSideLoading] = useState(false)
  const [sideError, setSideError] = useState('')

  const connected = !!profile?.hmrc_connected && !!profile?.hmrc_refresh_token
  const hasNino   = !!profile?.hmrc_nino

  // Single place that talks to the API route, so token rotation is handled once.
  // HMRC refresh tokens are SINGLE USE. Every call may rotate them, so the live
  // token is held in a ref and updated synchronously the moment a call returns a
  // new one. Reading tokens from React state instead meant two concurrent calls
  // both sent the same token: the first consumed it and the second was rejected
  // as expired. The ref also keeps callHMRC's identity stable, which stops the
  // effect below from re-firing on every rotation.
  const tokensRef  = useRef(null)
  const profileRef = useRef(profile)
  profileRef.current = profile

  useEffect(() => {
    if (!tokensRef.current && profile?.hmrc_refresh_token) {
      tokensRef.current = {
        accessToken:  profile.hmrc_access_token,
        refreshToken: profile.hmrc_refresh_token,
        tokenExpiry:  profile.hmrc_token_expiry,
      }
    }
  }, [profile])

  const callHMRC = useCallback(async (action, extra = {}) => {
    const p = profileRef.current
    const t = tokensRef.current || {
      accessToken:  p?.hmrc_access_token,
      refreshToken: p?.hmrc_refresh_token,
      tokenExpiry:  p?.hmrc_token_expiry,
    }

    const clientPublicPort = await getClientPublicPort()

    const res = await fetch('/api/hmrc-data', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action,
        accessToken:  t.accessToken,
        refreshToken: t.refreshToken,
        tokenExpiry:  t.tokenExpiry,
        nino:         p?.hmrc_nino,
        taxYear,
        userId:       user?.id,
        deviceId:     getDeviceId(),
        userAgent:    navigator.userAgent,
        timezone:     Intl.DateTimeFormat().resolvedOptions().timeZone,
        screens:      `width=${screen.width}&height=${screen.height}&scaling-factor=${window.devicePixelRatio || 1}&colour-depth=${screen.colorDepth}`,
        // outerWidth/outerHeight are in screen coordinates and are unaffected by
        // page zoom, so the window can never be reported as wider than the
        // screen. innerWidth inflates when the browser is zoomed out.
        windowSize:   `width=${window.outerWidth}&height=${window.outerHeight}`,
        plugins:      Array.from(navigator.plugins || []).map(p2 => p2.name).join(',').substring(0, 500),
        doNotTrack:   navigator.doNotTrack,
        localIPs:     '',
        clientPublicPort,
        ...extra,
      }),
    })

    const data = await res.json()

    // Update the in-memory token first so the very next call uses it, then
    // persist. loadProfile is deliberately not called here: it would change
    // profile, rebuild this callback and re-run the loading effect.
    if (data.newRefreshToken) {
      tokensRef.current = {
        accessToken:  data.newAccessToken,
        refreshToken: data.newRefreshToken,
        tokenExpiry:  data.newExpiryTime,
      }
      if (user?.id) {
        await supabase.from('profiles').update({
          hmrc_access_token:  data.newAccessToken,
          hmrc_refresh_token: data.newRefreshToken,
          hmrc_token_expiry:  data.newExpiryTime,
        }).eq('id', user.id)
      }
    }

    if (!res.ok) throw new Error(data.error || 'HMRC returned an error')
    return data
  }, [user, taxYear])

  // ITSA status and previously submitted figures load together on mount.
  // Sequential, not parallel: each call may rotate the single-use refresh token,
  // so the second must run after the first has returned the replacement.
  const loadedRef = useRef(false)

  const loadSidePanels = useCallback(async () => {
    if (!connected || !hasNino) return
    setSideLoading(true); setSideError('')

    let ok = 0
    try {
      setItsa(await callHMRC('itsa-status')); ok++
    } catch (err) {
      setItsa({ error: err.message })
    }
    try {
      setSubmitted(await callHMRC('cumulative-retrieve', { businessId: profileRef.current?.hmrc_business_id })); ok++
    } catch (err) {
      setSubmitted({ error: err.message })
    }

    if (ok === 0) setSideError('Could not reach HMRC. Try reconnecting in Settings.')

    // Refresh the shared profile once, after all calls are done. HMRC refresh
    // tokens are single use and these calls rotate them; without this the rest
    // of the app (notably the submission page) keeps the superseded token and
    // fails with "session expired". Done once at the end, not per call, so it
    // cannot re-trigger this effect.
    if (loadProfile && user?.id) await loadProfile(user.id)

    setSideLoading(false)
  }, [connected, hasNino, callHMRC, loadProfile, user])

  useEffect(() => {
    if (loadedRef.current) return
    if (!connected || !hasNino) return
    loadedRef.current = true
    loadSidePanels()
  }, [connected, hasNino, loadSidePanels])

  async function runCalculation() {
    setCalcLoading(true); setCalcError(''); setCalc(null)
    try {
      const data = await callHMRC('calculation-trigger')
      if (!data.calculation) {
        setCalcError(
          data.calculationId
            ? 'HMRC has started your calculation but it is not ready yet. Try again in a moment.'
            : 'HMRC did not return a calculation for this tax year yet.'
        )
      }
      setCalc(data)
    } catch (err) {
      setCalcError(err.message)
    } finally {
      // Same reason as above: publish the rotated token to the rest of the app.
      if (loadProfile && user?.id) { try { await loadProfile(user.id) } catch {} }
      setCalcLoading(false)
    }
  }

  // ── Not connected / no NINO ─────────────────────────────────────────────
  if (!connected || !hasNino) {
    return (
      <div className="fade-in" style={{ maxWidth: 640 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, marginBottom: 6 }}>
          Your HMRC tax position
        </h1>
        <p style={{ fontSize: 13, color: 'var(--slate)', marginBottom: 24 }}>
          The official figures HMRC holds for you.
        </p>
        <div className="card" style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <AlertCircle size={20} color="var(--amber)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
              {connected ? 'Add your National Insurance number' : 'Connect your HMRC account'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.6, marginBottom: 14 }}>
              {connected
                ? 'We need your National Insurance number before HMRC will share your tax position.'
                : 'Connect Quarterly to HMRC to see your official calculation, MTD status and anything you owe.'}
            </div>
            <a href="/settings?tab=integrations" className="btn btn-primary btn-sm">Go to Settings</a>
          </div>
        </div>
      </div>
    )
  }

  const c        = calc?.calculation || null

  // Temporary diagnostic: shows exactly what HMRC returned and how it is judged.
  useEffect(() => {
    if (!calc) return
    const probe = v => ({ value: v, type: typeof v, num: Number(v), real: isRealAmount(v) })
    const s = calc?.calculation?.calculation?.taxCalculation || calc?.calculation?.taxCalculation || null
    console.log('[HMRC figures]', {
      incomeTax: probe(s?.incomeTax?.totalIncomeTaxDue ?? s?.incomeTax?.incomeTaxCharged),
      nics:      probe(s?.nics?.totalNic),
      totalDue:  probe(s?.totalIncomeTaxAndNicsDue ?? s?.totalIncomeTaxNicsCharged),
      moneyCheck: money(-99999999999999.99),
    })
  }, [calc])
  const summary  = c?.calculation?.taxCalculation || c?.taxCalculation || null
  const incomeTax = summary?.incomeTax?.totalIncomeTaxDue
                 ?? summary?.incomeTax?.incomeTaxCharged
                 ?? null
  const nics      = summary?.nics?.totalNic ?? null
  const totalDue  = summary?.totalIncomeTaxAndNicsDue
                 ?? summary?.totalIncomeTaxNicsCharged
                 ?? null
  const totalIncome = summary?.incomeTax?.totalIncomeReceivedFromAllSources
                   ?? c?.calculation?.totalIncomeReceived
                   ?? null

  return (
    <div className="fade-in" style={{ maxWidth: 780 }}>
      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, marginBottom: 6 }}>
        Your HMRC tax position
      </h1>
      <p style={{ fontSize: 13, color: 'var(--slate)', marginBottom: 24 }}>
        Official figures from HMRC for {taxYear}. These come directly from HMRC and may differ from the
        running estimate shown on your dashboard, which Quarterly calculates locally.
      </p>

      {/* ── Official calculation ───────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Calculator size={18} color="var(--teal)" />
            <div style={{ fontWeight: 600, fontSize: 15 }}>HMRC in-year tax calculation</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={runCalculation} disabled={calcLoading} style={{ flexShrink: 0 }}>
            {calcLoading ? <span className="spin" /> : <><RefreshCw size={13} /> Get calculation</>}
          </button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--slate)', marginBottom: 16 }}>
          Asks HMRC to run a calculation from everything submitted so far this tax year. This is an
          estimate for the year to date, not a demand for payment.
        </div>

        {calcLoading && (
          <div style={{ fontSize: 12, color: 'var(--slate)', padding: '12px 0' }}>
            HMRC is calculating. This usually takes about ten seconds.
          </div>
        )}

        {calcError && !calcLoading && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'rgba(245,166,35,.06)', border: '1px solid rgba(245,166,35,.3)', borderRadius: 8, padding: '12px 14px' }}>
            <AlertCircle size={15} color="var(--amber)" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 12, color: 'var(--slate)', lineHeight: 1.6 }}>{calcError}</div>
          </div>
        )}

        {summary && !calcLoading && (
          <div>
            {![totalIncome, incomeTax, nics, totalDue].some(isRealAmount) && (
              <div style={{ fontSize: 12, color: 'var(--slate)', marginBottom: 14 }}>
                HMRC returned a calculation but no usable figures for {taxYear} yet.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <Stat label="Total income" value={money(totalIncome)} />
              <Stat label="Income Tax" value={money(incomeTax)} />
              <Stat label="National Insurance" value={money(nics)} />
              <Stat label="Total due" value={money(totalDue)} highlight />
            </div>
            <div style={{
              background: 'rgba(255,255,255,.02)', border: '1px solid var(--navy-border)',
              borderRadius: 8, padding: '11px 13px', marginBottom: 12,
            }}>
              <div style={{ fontSize: 11, color: 'var(--slate)', lineHeight: 1.65 }}>
                This calculation is only based on information HMRC have received about your income
                and expenses to {shortDate(new Date())}. This may change as we receive further
                information about you during the tax year.
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--slate-dim)' }}>
              Calculation ID {calc.calculationId} · retrieved {shortDate(new Date()) }
            </div>
          </div>
        )}

        {calc && !summary && !calcError && !calcLoading && (
          <div style={{ fontSize: 12, color: 'var(--slate)' }}>
            HMRC accepted the request but has not returned figures for {taxYear} yet. This is normal
            before your first quarterly update for the year has been processed.
          </div>
        )}
      </div>

      {/* ── MTD status ─────────────────────────────────────────────────── */}
      {/* The HMRC account balance card previously sat alongside this. It used
          Self Assessment Accounts v4.0, which we will subscribe to and test
          after production approval (HMRC ticket 2026-KXP605), at which point
          the card can be restored. */}
      <div>

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <ShieldCheck size={17} color="var(--teal)" />
            <div style={{ fontWeight: 600, fontSize: 14 }}>MTD status</div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--slate)', marginBottom: 14 }}>
            Whether HMRC has you signed up for Making Tax Digital for {taxYear}.
          </div>
          {sideLoading && <div style={{ fontSize: 12, color: 'var(--slate-dim)' }}>Checking with HMRC…</div>}
          {!sideLoading && itsa?.status && (() => {
            // HMRC status values include: MTD Mandated, MTD Voluntary, Annual,
            // No Status, Non Digital, Dormant, MTD Exempt.
            const signedUp = /mandated|voluntary/i.test(itsa.status)
            const colour   = signedUp ? 'var(--teal)' : 'var(--amber)'
            return (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {signedUp
                    ? <CheckCircle size={14} color={colour} />
                    : <AlertCircle size={14} color={colour} />}
                  <span style={{ fontSize: 14, fontWeight: 600, color: colour }}>{itsa.status}</span>
                </div>
                {!signedUp && (
                  <div style={{ fontSize: 11, color: 'var(--slate)', marginTop: 8, lineHeight: 1.6 }}>
                    HMRC does not currently have you signed up for Making Tax Digital for this tax
                    year. You can still keep records in Quarterly, but quarterly updates may be
                    rejected until you are signed up.
                  </div>
                )}
              </div>
            )
          })()}
          {!sideLoading && itsa && !itsa.status && (
            <div style={{ fontSize: 12, color: 'var(--slate)' }}>
              {itsa.error || `HMRC has no MTD status recorded for ${taxYear}.`}
            </div>
          )}
        </div>

      </div>

      {/* ── What HMRC holds from our submissions ───────────────────────── */}
      <div className="card" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <FileCheck size={17} color="var(--teal)" />
          <div style={{ fontWeight: 600, fontSize: 14 }}>Figures HMRC holds for you</div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--slate)', marginBottom: 14 }}>
          Retrieved from HMRC, so you can check what they hold matches what Quarterly submitted.
        </div>
        {sideLoading && <div style={{ fontSize: 12, color: 'var(--slate-dim)' }}>Checking with HMRC…</div>}
        {!sideLoading && submitted?.submission && isRealAmount(submitted.submission.turnover) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Stat label="Turnover" value={money(submitted.submission.turnover)} />
            <Stat label="Expenses" value={money(submitted.submission.consolidatedExpenses)} />
            <Stat
              label="Period to"
              value={shortDate(submitted.submission.periodEndDate) || '—'}
            />
          </div>
        )}
        {!sideLoading && (!submitted?.submission || !isRealAmount(submitted?.submission?.turnover)) && (
          <div style={{ fontSize: 12, color: 'var(--slate)' }}>
            {submitted?.error || `Nothing submitted to HMRC for ${taxYear} yet. Your figures will appear here once you file your first quarterly update.`}
          </div>
        )}
      </div>

      {sideError && (
        <div style={{ marginTop: 14, fontSize: 12, color: 'var(--amber)' }}>{sideError}</div>
      )}

      <div style={{ marginTop: 24, fontSize: 11, color: 'var(--slate-dim)', lineHeight: 1.7 }}>
        Quarterly submits quarterly updates for self-employment income only. End of year and Final
        Declaration are not yet available in Quarterly — you can find software that supports them on the{' '}
        <a
          href="https://www.gov.uk/guidance/find-software-thats-compatible-with-making-tax-digital-for-income-tax"
          target="_blank"
          rel="noreferrer"
          style={{ color: 'var(--teal)', textDecoration: 'none' }}
        >
          HMRC compatible software list <ExternalLink size={10} style={{ display: 'inline', verticalAlign: 'baseline' }} />
        </a>.
      </div>
    </div>
  )
}

function Stat({ label, value, highlight }) {
  return (
    <div style={{
      background: highlight ? 'var(--teal-glow)' : 'rgba(255,255,255,.02)',
      border: `1px solid ${highlight ? 'rgba(0,212,170,.25)' : 'var(--navy-border)'}`,
      borderRadius: 8,
      padding: '12px 14px',
    }}>
      <div style={{ fontSize: 11, color: 'var(--slate)', marginBottom: 4 }}>{label}</div>
      <div style={{
        fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-display)',
        color: highlight ? 'var(--teal)' : 'var(--white)',
      }}>{value}</div>
    </div>
  )
}
